import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { fadeInUp, staggerContainer, staggerItem } from '../utils/animations';

const Industrial = () => {
  const navigate = useNavigate();
  
  return (
    <div className="pt-28 sm:pt-24">
      {/* Hero Section */}
      <section className="py-20 hero-gradient">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-dark mb-6">
              Industrial <span className="text-primary-brown">Solutions</span>
            </h1>
            <p className="text-xl text-dark-light leading-relaxed">
              Premium-grade finishes engineered for precision, durability, and timeless appeal.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Gallery */}
            <section className="py-20 bg-accent-cream">
              <div className="container mx-auto px-4">
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-3 gap-6"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {[
                    { id: 1, image: '/images/Industrial Picture 1.png', alt: 'Industrial Transformation 1' },
                    { id: 2, image: '/images/Industrial Picture 2.png', alt: 'Industrial Transformation 2' },
                    { id: 3, image: '/images/Industrial Picture 3.png', alt: 'Industrial Transformation 3' }
                  ].map((item) => (
                    <motion.div
                      key={item.id}
                      className="aspect-[3/4] rounded-lg overflow-hidden shadow-lg"
                      variants={staggerItem}
                    >
                      <img src={item.image} alt={item.alt} className="w-full h-full object-cover" />
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </section>

      {/* Service 1: Paint, Coatings & Construction Finishing */}
      <section id="paint-coatings" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle 
            title="Industrial Flooring Systems" 
            subtitle="Service 01"
            align="center"
          />
          
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-12">
            These flooring systems are engineered for durability, heavy load resistance, smooth hygiene-friendly surfaces, and long-term performance in high-traffic industrial environments.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="bg-accent-cream p-8 rounded-lg">
                <h3 className="text-2xl font-serif text-dark mb-4">Features</h3>
                <ul className="space-y-3 text-dark-light">
                  <li>• High-performance industrial coatings</li>
                  <li>• Corrosion-resistant solutions</li>
                  <li>• Chemical and weather resistance</li>
                  <li>• Long-lasting durability</li>
                  <li>• Professional application</li>
                </ul>
              </div>

              <div className="bg-primary-beige p-8 rounded-lg">
                <h3 className="text-2xl font-serif text-dark mb-4">Applications</h3>
                <ul className="space-y-3 text-dark-light">
                  <li>• Manufacturing facilities</li>
                  <li>• Warehouses and storage</li>
                  <li>• Industrial equipment</li>
                  <li>• Metal structures</li>
                  <li>• Factory floors</li>
                </ul>
              </div>

              <div className="bg-primary-tan p-8 rounded-lg">
                <h3 className="text-2xl font-serif text-dark mb-4">Flooring Types</h3>
                <ul className="space-y-3 text-dark-light">
                  <li>• Epoxy Flooring & Coating</li>
                  <li>• Polyurethane Flooring & Coating</li>
                  <li>• Impact-Resistant Floor Layers</li>
                </ul>
              </div>
            </div>

            {/* Detailed Subcategories */}
            <div className="space-y-8">
              <div className="bg-accent-cream p-8 rounded-lg">
                <h4 className="text-xl font-serif text-dark mb-4">Epoxy Flooring & Coating</h4>
                <p className="text-dark-light leading-relaxed">
                  Epoxy flooring provides a seamless, durable surface designed to withstand heavy use in industrial, commercial, and operational environments. It forms a strong protective layer that resists scratches, stains, and chemicals, while also being easy to clean and maintain. Epoxy can be customized in appearance and finish, making it suitable for both functional and visually refined spaces.
                </p>
              </div>

              <div className="bg-primary-beige p-8 rounded-lg">
                <h4 className="text-xl font-serif text-dark mb-4">Polyurethane Flooring & Coating</h4>
                <p className="text-dark-light leading-relaxed">
                  Polyurethane flooring is known for its flexibility, abrasion resistance, and smooth finish. It handles temperature changes, foot traffic, and mechanical stress effectively, making it ideal for areas where comfort underfoot and long-lasting performance are important. Its slightly softer surface compared to epoxy helps absorb impact and reduces wear over time.
                </p>
              </div>

              <div className="bg-primary-tan p-8 rounded-lg">
                <h4 className="text-xl font-serif text-dark mb-4">Impact-Resistant Floor Layers</h4>
                <p className="text-dark-light leading-relaxed">
                  Impact-resistant flooring systems are engineered to absorb shock and prevent damage in high-activity or heavy-load environments. These layers strengthen the flooring structure, reducing cracks, chips, and surface stress caused by equipment, machinery, or frequent movement. The result is a floor that retains its integrity and appearance even under demanding conditions.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service 2: Chemical & Environmental Protection */}
      <section id="texture-finishes" className="py-20 bg-accent-cream scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="Chemical & Environmental Protection"
            subtitle="Service 02"
            align="center"
          />

          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-12">
              Designed for facilities exposed to chemicals, spills, moisture, temperature fluctuations, and corrosive environments — ensuring surface integrity and safety compliance.
            </p>

            {/* Detailed Subcategories */}
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-lg">
                <h4 className="text-xl font-serif text-dark mb-4">Chemical-Resistant Coatings</h4>
                <p className="text-dark-light leading-relaxed">
                Chemical-resistant coatings are designed to protect surfaces from spills, splashes, and exposure to industrial chemicals. These coatings create a non-reactive barrier that prevents corrosion, staining, and structural damage. They are ideal for laboratories, manufacturing plants, processing areas, and any environment where chemical contact is a routine part of operations. The result is safer, longer-lasting surfaces with reduced maintenance requirements.
                </p>
              </div>

              <div className="bg-primary-beige p-8 rounded-lg">
                <h4 className="text-xl font-serif text-dark mb-4">Acid-Resistant Chlorinated Rubber Coatings</h4>
                <p className="text-dark-light leading-relaxed">
                Acid-resistant chlorinated rubber coatings offer strong protection in environments exposed to acids, alkalis, moisture, and harsh industrial conditions. They adhere well to concrete, metal, and masonry surfaces, forming a tough, durable layer that resists chemical breakdown over time. These coatings are commonly used in chemical plants, wastewater facilities, storage units, and industrial processing areas where surface integrity and safety are critical.
                </p>
              </div>

              
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service 3: Structural Protection & Maintenance */}
      <section id="interior-exterior" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="Structural Protection & Maintenance"
            subtitle="Service 03"
            align="center"
          />

          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-12">
              Focused on extending the lifespan of operational spaces by preventing wear, corrosion, and surface degradation across factories, warehouses, and production areas.
            </p>

            {/* Detailed Subcategories */}
            <div className="space-y-8">
              <div className="bg-accent-cream p-8 rounded-lg">
                <h4 className="text-xl font-serif text-dark mb-4">Protective Coatings</h4>
                <p className="text-dark-light leading-relaxed">
                Protective coatings are applied to shield surfaces from wear, moisture, UV exposure, corrosion, and general environmental impact. These coatings improve surface durability and extend the lifespan of structures and equipment in both indoor and outdoor conditions. They help maintain a clean, stable finish while reducing repair and replacement costs over time. Ideal for facilities where reliability and long-term performance matter.
                </p>
              </div>

              <div className="bg-primary-beige p-8 rounded-lg">
                <h4 className="text-xl font-serif text-dark mb-4">Maintenance and Restoration Solutions</h4>
                <p className="text-dark-light leading-relaxed">
                Maintenance and restoration services help preserve existing surfaces and restore worn or damaged flooring and coatings. This includes surface assessment, repair, resurfacing, and re-application of protective layers to bring the space back to optimal condition. These solutions restore performance, prevent further deterioration, and ensure the environment remains safe and visually consistent. Suitable for active facilities aiming to maintain uninterrupted operations.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-brown text-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif mb-6">
              Ready to Transform Your Industrial Space?
            </h2>
            <p className="text-xl mb-8 text-primary-beige">
              Let's create something extraordinary together.
            </p>
            <Button variant="secondary" size="large" onClick={() => navigate('/contact')}>
              Get In Touch
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industrial;